import { useInView, IntersectionOptions } from 'react-intersection-observer';
import {
  CtaEventDataType,
  trackCtaClicked,
  trackCtaViewed,
} from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { useEffect } from 'react';

interface WithCtaTrackingProps {
  trackingPayload?: CtaEventDataType;
  onClick?: React.MouseEventHandler<HTMLElement>;
  className?: string;
}

interface WithCtaTrackingOptions {
  intersectionOptionsOverride?: IntersectionOptions;
}

/**
 * TO USE:
 * Wrap component to be tracked--------
 * ex. withCtaTracking(MarketingModuleSection);
 *
 * if tracking payload dynamically derived from props----
 * Create intermediary component---
 * ex.
 * const MarketingModuleSectionWithTracking = (props) => {
 *  const {index, type} = props;
 *  const trackingPayload = {
 *    position: index,
 *    cta_id: ...,
 *    ...
 *  };
 *  const TrackedMarketingModuleSection = withCtaTracking(MarketingModuleSection);
 *  return <TrackedMarketingModuleSection trackingPayload={trackingPayload} />;
 * }
 *
 * if tracking payload static-----
 * Pass through hoc params----
 * ex.
 * const MarketingModuleSectionWithTracking = withCtaTracking(MarketingModuleSection, {}, trackingPayload);
 *
 * ex. <MarketingModuleSection trackingPayload={} ... />
 */
function withCtaTracking<P extends object>(
  Component: React.ComponentType<P>,
  options: WithCtaTrackingOptions = {},
  trackingPayloadParam?: CtaEventDataType
): React.FC<P & WithCtaTrackingProps> {
  const ComponentWithCtaTracking: React.FC<P & WithCtaTrackingProps> = ({
    trackingPayload: trackingPayloadProp,
    onClick,
    className,
    ...passThroughProps
  }) => {
    const { intersectionOptionsOverride = {} } = options;
    const defaultOptions = {
      triggerOnce: true,
    };
    const intersectionOptionsFinal = Object.assign(intersectionOptionsOverride, defaultOptions);
    const trackingPayload = trackingPayloadProp || trackingPayloadParam;
    // hook that calls track cta viewed event at most once
    const inViewResponse = useInView(intersectionOptionsFinal);
    const { inView, ref } = inViewResponse;
    useEffect(() => {
      if (inView && trackingPayload) {
        trackCtaViewed(trackingPayload);
      }
    }, [inView, trackingPayload]);

    // to explicitly track click
    const trackClick = () => {
      // trigger track cta clicked
      if (trackingPayload) {
        trackCtaClicked(trackingPayload);
      }
    };

    // to implicitly track click
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      trackClick();
      if (onClick) onClick(event);
    };

    return (
      <>
        {/* eslint-disable-next-line */}
        <div ref={ref} className={className} onClick={handleClick}>
          <Component {...(passThroughProps as P)} />
        </div>
      </>
    );
  };

  const displayName = Component.displayName || Component.name || 'Component';
  ComponentWithCtaTracking.displayName = `withCtaTracking(${displayName})`;

  return ComponentWithCtaTracking;
}

export default withCtaTracking;
